export type PaginationItem =
  | { type: 'page'; page: number; key: string }
  | { type: 'overflow'; pages: number[]; key: string; ariaLabel: string }

export type GetPaginationDisplayItemsParams = {
  total: number
  current: number
  maxInline: number
  i18n: (locale: string, key: string) => string
  locale: string
}

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

export function getPaginationDisplayItems({
  total,
  current,
  maxInline,
  i18n,
  locale,
}: GetPaginationDisplayItemsParams): PaginationItem[] {
  if (total <= maxInline) {
    return range(1, total).map((page) => ({
      type: 'page',
      page,
      key: `page-${page}`,
    }))
  }

  const items: PaginationItem[] = []
  const firstPage = 1
  const lastPage = total
  const slots = maxInline - 2

  const addPage = (page: number) => {
    if (!items.some((item) => item.type === 'page' && item.page === page)) {
      items.push({ type: 'page', page, key: `page-${page}` })
    }
  }

  const pushOverflow = (pages: number[], key: string, ariaLabel: string) => {
    if (pages.length > 1) {
      items.push({ type: 'overflow', pages, key, ariaLabel })
    } else if (pages.length === 1 && pages[0] !== undefined) {
      addPage(pages[0])
    }
  }

  addPage(firstPage)

  if (current <= slots) {
    range(2, slots + 1).forEach(addPage)
    const rightOverflowPages = range(slots + 2, lastPage - 1)
    pushOverflow(
      rightOverflowPages,
      'overflow-right',
      i18n(locale, 'morePagesRight'),
    )
  } else if (current > total - slots) {
    const firstInline = total - slots
    const leftOverflowPages = range(2, firstInline - 1)
    pushOverflow(
      leftOverflowPages,
      'overflow-left',
      i18n(locale, 'morePagesLeft'),
    )
    range(firstInline, lastPage - 1).forEach(addPage)
  } else {
    const sideSlots = Math.floor((slots - 1) / 2)
    const leftOverflowPages = range(2, current - sideSlots - 1)
    pushOverflow(
      leftOverflowPages,
      'overflow-left',
      i18n(locale, 'morePagesLeft'),
    )
    range(current - sideSlots, current + sideSlots).forEach(addPage)
    const rightOverflowPages = range(current + sideSlots + 1, lastPage - 1)
    pushOverflow(
      rightOverflowPages,
      'overflow-right',
      i18n(locale, 'morePagesRight'),
    )
  }

  addPage(lastPage)

  return items
}
